// Frontend service for Zearch API calls
const ZEARCH_API_BASE = process.env.REACT_APP_ZEARCH_API_URL || 'https://zpost.ai/api/zearch';

// Helper to get auth headers (matches your AuthContext)
const getAuthHeaders = () => {
  const token = localStorage.getItem('zhat_token'); // Same token as AuthContext
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

export const zearchService = {
  // Main search function - WITH AUTH HEADERS
  async search(query, type = 'all', limit = 12) {
    try {
      const headers = getAuthHeaders();
      
      // Check if we have a token
      if (!headers.Authorization) {
        console.warn('⚠️ No auth token found for Zearch search');
        return {
          success: false,
          error: 'Authentication required',
          results: {
            ens: [],
            zpost: [],
            web: [],
            news: [],
            videos: [],
            images: []
          }
        };
      }
      
      const response = await fetch(
        `${ZEARCH_API_BASE}/search?q=${encodeURIComponent(query)}&type=${type}&limit=${limit}`,
        {
          headers: headers
        }
      );
      
      if (!response.ok) {
        // Handle 401 specifically
        if (response.status === 401) {
          console.error('Zearch: Authentication failed (401)');
          return {
            success: false,
            error: 'Authentication expired. Please log in again.',
            needsLogin: true,
            results: {
              ens: [],
              zpost: [],
              web: [],
              news: [],
              videos: [],
              images: []
            }
          };
        }
        throw new Error(`Search failed: ${response.status}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error('Search service error:', error);
      return {
        success: false,
        error: error.message,
        results: {
          ens: [],
          zpost: [],
          web: [],
          news: [],
          videos: [],
          images: []
        }
      };
    }
  },

  // Get trending searches - WITH AUTH HEADERS
  async getTrending() {
    try {
      const headers = getAuthHeaders();
      
      if (!headers.Authorization) {
        console.warn('⚠️ No auth token found for Zearch trending');
        return { success: false, trends: [], needsLogin: true };
      }
      
      const response = await fetch(`${ZEARCH_API_BASE}/trending`, {
        headers: headers
      });
      
      if (response.status === 401) {
        console.error('Zearch trending: Authentication failed (401)');
        return { success: false, trends: [], needsLogin: true };
      }
      
      return await response.json();
    } catch (error) {
      console.error('Trending service error:', error);
      return { success: false, trends: [] };
    }
  },

  // Health check - NO AUTH NEEDED (public endpoint)
  async healthCheck() {
    try {
      const response = await fetch(`${ZEARCH_API_BASE}/health`);
      return await response.json();
    } catch (error) {
      return { status: 'unhealthy', error: error.message };
    }
  },

  // Save search to local storage
  saveToRecent(query) {
    try {
      const recent = JSON.parse(localStorage.getItem('zearch_recent') || '[]');
      const updated = [
        { query, timestamp: Date.now() },
        ...recent.filter(item => item.query !== query)
      ].slice(0, 10);
      
      localStorage.setItem('zearch_recent', JSON.stringify(updated));
      return updated;
    } catch (error) {
      console.error('Save recent error:', error);
      return [];
    }
  },

  // Get recent searches
  getRecent() {
    try {
      return JSON.parse(localStorage.getItem('zearch_recent') || '[]');
    } catch (error) {
      return [];
    }
  },

  // Clear recent searches
  clearRecent() {
    localStorage.removeItem('zearch_recent');
  }
};

export default zearchService;
